export type OpsPlatformHealthStatus = 'healthy' | 'degraded' | 'critical';

export type ProcessorOpsHealthStatus = 'healthy' | 'degraded' | 'critical';

export type OpsProcessorExhaustionDto = {
  connected: number;
  enrolled: number;
  busy: number;
  idle: number;
  maintenance: number;
  offline: number;
  queueDepth: number;
  inFlightCount: number;
  escalatedCount: number;
  oldestQueuedAgeSeconds: number | null;
  poolExhausted: boolean;
  poolEmptyWithBacklog: boolean;
};

export type ProcessorOpsStatisticsDto = {
  collectedAt: string;
  pool: {
    connectedCount: number;
    enrolledCount: number;
    busyCount: number;
    idleCount: number;
    maintenanceCount: number;
    offlineCount: number;
  };
  queue: {
    queueDepth: number;
    inFlightCount: number;
    escalatedCount: number;
    oldestQueuedAgeSeconds: number | null;
  };
  throughput: {
    sampleCount: number;
    windowHours: number;
    averageThroughputSeconds: number | null;
    averageProcessingSeconds: number | null;
    p50ThroughputSeconds: number | null;
  };
  waitEstimates: {
    parallelism: number;
    expectedWaitNewJobSeconds: number | null;
    expectedWaitOldestQueuedSeconds: number | null;
    averageExpectedWaitQueuedSeconds: number | null;
  };
  health: {
    status: ProcessorOpsHealthStatus;
    reasons: string[];
  };
};

export type OpsSingleCamExhaustionDto = {
  enrolled: number;
  free: number;
  allocated: number;
  allocatedConnected: number;
  allocatedOffline: number;
  allocatedOfflineWhileLive: number;
  maintenance: number;
  totalBacklog: number;
  flowsWithBacklog: number;
  oldestQueuedAgeSeconds: number | null;
  poolExhausted: boolean;
  poolEmptyWithBacklog: boolean;
  queueStuckWithFreeAgent: boolean;
  allocatedOfflineWhileLiveSignal: boolean;
};

export type SingleCamAgentOpsStatisticsDto = {
  collectedAt: string;
  pool: {
    enrolledCount: number;
    freeCount: number;
    allocatedCount: number;
    allocatedConnectedCount: number;
    allocatedOfflineCount: number;
    allocatedOfflineWhileLiveCount: number;
    maintenanceCount: number;
  };
  queue: {
    totalBacklog: number;
    flowsWithBacklog: number;
    oldestQueuedAgeSeconds: number | null;
  };
  allocation: {
    offlineAgents: Array<{
      playerId: string;
      allocatedStagePlayerId: string;
      whileLive: boolean;
    }>;
  };
  exhaustion: {
    poolEmptyWithBacklog: boolean;
    poolExhausted: boolean;
    queueStuckWithFreeAgent: boolean;
    allocatedOfflineWhileLive: boolean;
  };
};

export type MediaAgentOpsStatisticsDto = {
  collectedAt: string;
  pool: {
    total: number;
    connected: number;
    free: number;
    allocated: number;
    maintenance: number;
  };
  bookings: {
    pending: number;
    assigned: number;
    active: number;
    failed: number;
    upcoming24h: number;
    upcoming48h: number;
    peakParallelRequired48h: number;
    activeShellCapacity: number;
    peakShortfall48h: number;
    demandTimeline48h: Array<{
      at: string;
      required: number;
      capacity?: number;
      covered: number;
      shortfall: number;
      spareCapacity: number;
    }>;
    peakAt: string | null;
  };
  health: {
    status: ProcessorOpsHealthStatus;
    reasons: string[];
  };
};

export type OpsMediaAgentExhaustionDto = {
  total: number;
  connected: number;
  free: number;
  allocated: number;
  maintenance: number;
  pendingBookings: number;
  assignedBookings: number;
  failedBookings: number;
  upcoming24h: number;
  upcoming48h: number;
  peakParallelRequired48h: number;
  activeShellCapacity: number;
  peakShortfall48h: number;
  demandTimeline48h: Array<{
    at: string;
    required: number;
    capacity?: number;
    covered: number;
    shortfall: number;
    spareCapacity: number;
  }>;
  peakAt: string | null;
  poolEmptyWithUpcomingBookings: boolean;
  bookingGaps: boolean;
  peakDemandExceedsCapacity: boolean;
  belowBaseline: boolean;
};

export type OpsPlatformSummaryDto = {
  collectedAt: string;
  health: {
    status: OpsPlatformHealthStatus;
    activeIncidents: number;
    degradedDomains: string[];
    reasons: string[];
  };
  domains: {
    processor: {
      enabled: boolean;
      exhaustion: OpsProcessorExhaustionDto | null;
      statistics: ProcessorOpsStatisticsDto | null;
      health: ProcessorOpsHealthStatus | null;
    };
    singlecam: {
      enabled: boolean;
      exhaustion: OpsSingleCamExhaustionDto | null;
      statistics: SingleCamAgentOpsStatisticsDto | null;
      health: ProcessorOpsHealthStatus | null;
    };
    mediaAgent: {
      enabled: boolean;
      exhaustion: OpsMediaAgentExhaustionDto | null;
      statistics: MediaAgentOpsStatisticsDto | null;
      health: ProcessorOpsHealthStatus | null;
    };
  };
};

export type OpsAttentionActionType =
  | 'retry_failed'
  | 'requeue'
  | 'release_processor'
  | 'reset_processor'
  | 'prepare_processor'
  | 'prepare_singlecam_agent'
  | 'prepare_media_agent'
  | 'reset_media_agent_booking'
  | 'reissue_media_agent_token'
  | 'recover_media_agent'
  | 'reissue_processor_token'
  | 'recover_processor'
  | 'activate_vm';

/** Media-agent attention rows raised by the auto-heal watchdog once automation is stuck. */
export const MEDIA_AGENT_HEAL_ATTENTION_TYPES = {
  JWT_STALE: 'media_agent_jwt_stale',
  NEEDS_REPAIR: 'media_agent_needs_repair',
  RECOVER_STUCK: 'media_agent_recover_stuck',
} as const;

export type MediaAgentHealAttentionType =
  (typeof MEDIA_AGENT_HEAL_ATTENTION_TYPES)[keyof typeof MEDIA_AGENT_HEAL_ATTENTION_TYPES];

/** Processor attention rows raised by the auto-heal watchdog once automation is stuck. */
export const PROCESSOR_HEAL_ATTENTION_TYPES = {
  JWT_STALE: 'processor_jwt_stale',
  NEEDS_REPAIR: 'processor_needs_repair',
  RECOVER_STUCK: 'processor_recover_stuck',
} as const;

export type OpsAttentionRowDto = {
  id: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  domain: 'processor' | 'singlecam' | 'media-agent' | 'vm';
  ageSeconds: number | null;
  summary: string;
  primaryAction: {
    type: OpsAttentionActionType;
    label: string;
  };
  context: {
    mediaId?: string;
    assetVersion?: number;
    playerId?: string;
    mediaTitle?: string;
    errorCode?: string;
    processorName?: string;
    vmId?: string;
    flowsWithBacklog?: number;
    stagePlayerId?: string;
    remediationHint?: string;
    bookingId?: string;
    failureReason?: string;
    recoverMode?: 'auto' | 'soft' | 'hard';
  };
  deepLink?: string;
};

export type OpsAttentionQueueDto = {
  collectedAt: string;
  rows: OpsAttentionRowDto[];
};
